import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { NumberInputField, TextInputField, RadioItemListArrays } from '../../../components/input';
import Typography from '@material-ui/core/Typography';
import update from 'immutability-helper';
import Alert from '@material-ui/lab/Alert';
import Button from '../../../components/Button';
import Loader from '../../../components/Loader';
import AccreditationSummary from '../../../components/AccreditationSummary';
import { ProviderAPI, BillingAPI } from '../../../api';
import equal from 'fast-deep-equal/es6/react';
import { dataDiff } from '../../../lib/util';

// map the 3 values of accredited_by_admin to ints and back for the radio selector
const tristateToIndex = {null: 0, true: 1, false: 2};
const indexToTristate = {0: null, 1: true, 2: false};

const useStyles = makeStyles({
  root: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    justifyContent: 'flex-start',
    padding: '0',
  },
  title: {
    marginBottom: '1rem',
  },
  controls: {
    padding: '1rem 0',
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    marginLeft: '1rem',
  },
  block: {
    marginTop: '1rem',
  },
});

const AccountSettings = ({providerUuid}) => {
  const classes = useStyles();
  const [busy, setBusy] = React.useState(false);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertState, setAlterState] = React.useState(null);
  const [alertCaption, setAlterCaption] = React.useState('');
  const [initial, setInitial] = React.useState(null);
  const [data, setData] = React.useState(null);
  const [subscription, setSubscription] = React.useState();

  const getData = async () => {
    try {
      const { payload } = await ProviderAPI.getProfile(providerUuid);
      const initialised = {};
      Object.keys(payload).forEach(k => {
        if (!Array.isArray(payload[k])) {
          initialised[k] = payload[k];
        }
      });
      setInitial(initialised);
      setData(initialised);
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  };

  const getSubscriptionData = async () => {
    try {
      const { payload } = await BillingAPI.getSubscription(providerUuid);
      setSubscription(payload.subscription);
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  };

  const handleChange = (event, field) => {
    const value = event.target.value;
    const updatedData = update(data, {[field]: {$set: value}});
    setData(updatedData);
  };

  const triggerAlert = (caption, variant) => {
    setAlterCaption(caption);
    setAlterState(variant);
    setAlertOpen(true);
  };

  const closeAlert = () => {
    setAlertOpen(false);
  };

  const save = async () => {
    if (equal(initial, data)) {
      triggerAlert('No data has changed.', 'info');
      return null;
    }
    setBusy(true);
    try {
      const patchData = dataDiff(initial, data);  // Only submit what has changed
      await ProviderAPI.adminUpdateProfile(providerUuid, patchData);
      setInitial(data);
      getSubscriptionData();
      triggerAlert('Provider profile successfully updated.', 'success');
    } catch (err) {
      const defaultMessage = 'Update failed. Check you have entered all information correctly.';
      const message = err.body && err.body.message ? err.body.message : defaultMessage;
      triggerAlert(message, 'warning');
    }
    setBusy(false);
  };

  React.useEffect(() => {
    getData();
    getSubscriptionData();
  }, []);  // eslint-disable-line

  if (data === null) {
    return (<Loader />);
  }

  return (
    <div className={classes.root}>
      <NumberInputField
        label="Additional Listings"
        value={data.extra_listings}
        onChange={(event) => handleChange(event, 'extra_listings')}
      />
      <TextInputField
        label="URL Suffix (slug)"
        value={data.slug}
        onChange={(event) => handleChange(event, 'slug')}
      />
      <div className={classes.block}>
        <Typography variant="subtitle2" className={classes.label}>
          Accreditation
        </Typography>
        <AccreditationSummary subscription={subscription}/>
        <RadioItemListArrays
          items={[
            {id: 0, name: 'Automatic'},
            {id: 1, name: 'Accredited'},
            {id: 2, name: 'Not Accredited'}]}
          value={tristateToIndex[data.accredited_by_admin]}
          onChange={e => setData({...data, 'accredited_by_admin': indexToTristate[e.target.value]})}
          levels="single"
        />
      </div>

      <div className={classes.controls}>
        {alertOpen ? (
          <Alert severity={alertState} onClose={closeAlert}>
            {alertCaption}
          </Alert>
        ) : null}
        <Button
          variant="contained"
          color="primary"
          onClick={save}
          label="Save"
          busy={busy}
          className={classes.button}
          pointer="none"
        />
      </div>
    </div>
  );
};

AccountSettings.propTypes = {
  providerUuid: PropTypes.string.isRequired,
};

export default AccountSettings;
